import { Avatar } from '@sortlist-frontend/design-system/legacy';
import { ImageSourceHelper } from '@sortlist-frontend/media';
import { Trans, useTranslation } from '@sortlist-frontend/translation/ssr';
import { FC, Fragment, useState } from 'react';

import { DirectBriefingButton } from '_components/Briefing/versions/DirectBriefing/button';
import { Dialog } from '_components/styled/dialog';
import { usePublicAppContext } from '_core/context/public-app-context';
import { WorkNode } from '_features/AgencyProfile/components/PortfolioPanel/types';

import { workDetailModalConfig } from './config';
import { useGetWork, type WorkDetail } from './hooks/useGetWork';
import { WorkDetailPanel } from './WorkDetailPanel';
import { WorkNavigation } from './WorkNavigation';

type Props = {
  open: boolean;
  onClose: () => void;
  workSlugs: string[] | undefined;
  works?: WorkNode[];
  index: number;
  workSlug: string;
  longtailsPage?: boolean;
};

export const WorkDetailModal = (props: Props) => {
  const { open, onClose, workSlugs, workSlug, works, index, longtailsPage } = props;
  const { t } = useTranslation(workDetailModalConfig.i18nNamespaces);
  const { locale } = usePublicAppContext();
  const [currentSlug, setCurrentSlug] = useState<string>(workSlug);
  const [currentIndex, setCurrentIndex] = useState<number>(index);

  const { data, loading } = useGetWork({ workSlug: currentSlug, locale });
  const currentWork: WorkDetail | undefined = data?.work;

  const agencyName = currentWork?.agency.name ?? '';
  const agencySlug = currentWork?.agency.slug ?? '';
  const agencyLogoUrl =
    currentWork?.agency.logoUrl ?? ImageSourceHelper.fromPath('/_img/default/default-image-square.svg');

  const similarWorks = works?.filter(({ slug }) => slug !== workSlug);
  const lastSlide = workSlugs != null ? currentIndex === workSlugs.length - 1 : true;

  const changeWorkSlug = (index: number) => {
    if (workSlugs == null) return;
    setCurrentIndex(index);
    setCurrentSlug(workSlugs[index]);
  };

  const nextWork = () => {
    if (lastSlide) return;
    changeWorkSlug(currentIndex + 1);
  };

  const prevWork = () => {
    if (!currentIndex) return;
    changeWorkSlug(currentIndex - 1);
  };

  const openSimilarWork = (work: WorkNode) => {
    setCurrentSlug(work.slug);
  };

  const header = (
    <div className="layout-row flex-1 layout-align-start-center px-4 sm:px-16 pt-8 pb-4">
      <Avatar
        fit="contain"
        src={ImageSourceHelper.fromUrl(agencyLogoUrl, {
          width: 150,
          quality: 75,
          staticFrame: true,
        })}
        alt={agencyName}
        avatarUiName={agencyName}
        size="sm"
        shape="square"
        className="mr-4"
      />
      <div className="layout-column lh-1 flex-1">
        <h4 className="bold text-truncate">{currentWork?.name}</h4>
        <span className="small text-truncate">
          <Trans i18nKey={'portfolio:work.byAgencyName'}>By {{ agencyName }}</Trans>
        </span>
      </div>
    </div>
  );

  const footer = (
    <div className="layout-row layout-align-start-center p-2 shadow-2 hide-gt-xs">
      <DirectBriefingButton
        className="width-100"
        cta="work-modal-footer"
        agencySlug={agencySlug as string}
        size={'sm'}
        buttonStyle="primary"
        buttonVariant="raised"
        label={t('portfolio:cta.btnLabel')}
      />
    </div>
  );

  if (currentWork == null) return null;

  return (
    <Fragment>
      <Dialog header={header} footer={footer} onClose={onClose} open={open} maxWidth={'md'}>
        <WorkDetailPanel
          agencyName={agencyName}
          agencySlug={agencySlug}
          isLoading={loading}
          work={currentWork}
          similarWorks={similarWorks}
          onClick={openSimilarWork}
          longtailsPage={longtailsPage}
        />
      </Dialog>
      {open && <WorkNavigation index={currentIndex} lastSlide={lastSlide} prevWork={prevWork} nextWork={nextWork} />}
    </Fragment>
  );
};
